import { useState } from 'react';
import {
  isLivePrState,
  isNewerPrSnapshot,
  parseGitHubPrUrl,
  prStateFromGitHub,
  type PrAssociationState,
} from '@reflex/shared';
import { usePullRequestDetails } from '@reflex/plugin-github/web/useGitHubRepos';
import { PullRequestChip } from '@reflex/ui/components/shared/PullRequestChip';
import type { PrDisplayState } from '@reflex/ui/components/shared/pr-state-presentation';

export interface AgentPRChipProps {
  /** Pull request URL from the agent's `pull_request` association. */
  prUrl: string;
  /**
   * The agent's relationship to the PR: it opened it (`creator`) or reviewed
   * someone else's (`reviewer`). Shown in the hover popover; omitted for
   * callers that render a bare PR link with no agent context.
   */
  role?: 'creator' | 'reviewer';
  /**
   * The PR's stored lifecycle state (`open` / `draft` / `merged` / `closed`)
   * from the association. Used as the starting point; the chip verifies a
   * still-live PR against GitHub (the stored value can be stale if a webhook
   * was missed or the org has no GitHub App installed) and reflects the live
   * state instead. Defaults to `open`.
   */
  state?: PrAssociationState;
  /**
   * The stored state's version — GitHub's `updated_at` the webhook read it at,
   * epoch ms — or `null` when the CLI recorded it at registration. Decides
   * whether the stored state or the live fetch is the newer answer.
   */
  stateUpdatedAt?: number | null;
  className?: string;
  /** Collapse to the state icon and border. See {@link PullRequestChip}. */
  iconOnly?: boolean;
  /**
   * Whether an `open` PR is verified against GitHub eagerly (on mount) to
   * reconcile a stale stored state (a missed merge/close webhook), vs. lazily
   * on the first hover-card open. Defaults to `true` — the focused views (the
   * stream header, the detail identity) want a merged PR to stop reading
   * `open` without the user having to hover. List rows pass `false` so a page
   * of N agents doesn't fire N background `pulls/:number` fetches on load (the
   * Sentry `AGENT-FLOW-WEB-4F` N+1). A lazy chip still corrects its state the
   * instant the hover card opens, matching how `merged`/`closed` PRs already
   * behave.
   */
  eagerVerifyOpenPrs?: boolean;
  'data-testid'?: string;
}

/**
 * The agent surfaces' pull-request chip: a {@link PullRequestChip} that
 * resolves the PR's live GitHub state, so a merged or closed PR stops reading
 * as open even when the stored association state is stale, and names the
 * agent's role in the card's footer.
 */
export function AgentPRChip({
  prUrl,
  role,
  state = 'open',
  stateUpdatedAt = null,
  className,
  iconOnly,
  eagerVerifyOpenPrs,
  'data-testid': dataTestId,
}: AgentPRChipProps) {
  const parsed = parseGitHubPrUrl(prUrl);
  const [open, setOpen] = useState(false);

  // A live (`open` / `draft`) PR is the stale-prone case — a missed webhook, or
  // a repo with no GitHub App installed, leaves the stored state behind — so
  // verify it eagerly, unless the host strip opted out (a list row rendering
  // many agents, where eager per-row fetches become an N+1). Merged/closed are
  // terminal and trusted, so those only fetch lazily when the card opens — to
  // fill in the title and branch flow.
  const verifyEagerly = isLivePrState(state) && (eagerVerifyOpenPrs ?? true);
  const { data, isLoading } = usePullRequestDetails(
    parsed ? { owner: parsed.owner, repo: parsed.repo, number: parsed.number } : null,
    { enabled: verifyEagerly || open },
  );

  // Render whichever of the stored state and the live fetch is newer. The
  // cached fetch can be minutes old — a merge the webhook already stored must
  // not be hidden behind it — while an unversioned stored state (recorded at
  // registration, or a repo with no GitHub App to send webhooks) yields to any
  // fetch. The greyed "checking" look is deliberately not used here: flashing
  // it on every mount would reintroduce the churn this chip is meant to stop.
  // `loading` still reaches the chip for the card.
  const stored = { state, updatedAt: stateUpdatedAt };
  const live = data ? { state: prStateFromGitHub(data), updatedAt: data.updatedAt } : null;
  const displayState: PrDisplayState =
    live && isNewerPrSnapshot(live, stored) ? live.state : stored.state;

  return (
    <PullRequestChip
      url={prUrl}
      state={displayState}
      details={data ?? null}
      loading={isLoading}
      onOpenChange={setOpen}
      footnote={
        role === 'reviewer'
          ? 'Reviewed by this agent'
          : role === 'creator'
            ? 'Opened by this agent'
            : null
      }
      iconOnly={iconOnly}
      className={className}
      testIdPrefix={dataTestId ?? 'agent-pr-chip'}
    />
  );
}
